import { Link } from 'react-router-dom'
import { FiHome, FiCheckCircle, FiAlertTriangle, FiShield } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'marco-legal', label: 'Marco legal del teletrabajo en España' },
  { id: 'aplica-fichaje', label: '¿Aplica el registro de jornada al teletrabajo?' },
  { id: 'desconexion', label: 'Derecho a la desconexión digital' },
  { id: 'metodos', label: 'Métodos de fichaje remoto recomendados' },
  { id: 'privacidad', label: 'Privacidad: lo que NO puedes hacer' },
  { id: 'acuerdo', label: 'Qué debe contener el acuerdo de teletrabajo' },
  { id: 'caso-practico', label: 'Caso práctico: pyme híbrida' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿El teletrabajador tiene que fichar igual que el presencial?', answer: 'Sí. La obligación del art. 34.9 ET no distingue por modalidad. La diferencia está en el método: en lugar de tornos o tarjeta, se usa app móvil o web con autenticación.' },
  { question: '¿Puedo monitorizar el ordenador del teletrabajador?', answer: 'Sí, dentro de límites estrictos. La monitorización debe ser proporcional, informada y orientada a fines legítimos. La AEPD ha sancionado a empresas que tomaban capturas continuas o registraban pulsaciones de teclado sin información previa.' },
  { question: '¿Quién paga el material y la conexión?', answer: 'La Ley 10/2021 establece que la empresa debe dotar de medios y compensar gastos. El convenio colectivo o acuerdo de empresa cuantifica el importe. Compensaciones típicas: 20-50 € mensuales según puesto.' },
  { question: '¿Es legal que el sistema active la cámara para verificar identidad?', answer: 'Solo con consentimiento explícito y proporcionalidad muy justificada. Para fichar, alternativas menos invasivas (PIN, sesión autenticada) son siempre preferibles. La biometría facial sin habilitación legal es de alto riesgo.' },
  { question: '¿Cómo se controlan las horas extras en teletrabajo?', answer: 'Mediante el propio sistema de fichaje. El empleado solicita autorización antes de superar la jornada y la dirección aprueba o deniega. Sin sistema, las horas extras se pierden o generan reclamaciones a posteriori.' },
]

export default function FichajeTeletrabajoRemoto() {
  return (
    <BlogLayout slug="fichaje-teletrabajo-remoto" toc={toc} faqs={faqs}>
      <section id="marco-legal" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Marco legal del teletrabajo en España</h2>
        <p>
          El teletrabajo en España se regula desde 2021 por la <strong>Ley 10/2021, de 9 de julio, de trabajo a distancia</strong>. Considera "trabajo a distancia" cuando se realiza en domicilio o lugar libremente elegido por el trabajador durante al menos un 30 % de la jornada en un periodo de tres meses.
        </p>
        <p>
          Junto al RD-ley 8/2019 (registro de jornada) y al RGPD, conforma el triángulo normativo que toda empresa con teletrabajo debe respetar. A este marco se sumará pronto <Link to="/blog/real-decreto-2026-fichaje-digital">el nuevo Real Decreto de fichaje digital previsto para 2026</Link>, que endurece los requisitos técnicos del registro.
        </p>
      </section>

      <section id="aplica-fichaje" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">¿Aplica el registro de jornada al teletrabajo?</h2>
        <p>
          <strong>Sí, sin excepciones.</strong> El art. 34.9 ET no distingue por lugar de prestación. La Ley 10/2021 lo recuerda en su art. 14: la empresa debe garantizar el registro horario también en teletrabajo.
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Los teletrabajadores fichan inicio y fin de jornada como cualquier otro empleado.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> El método debe ser fiable, accesible y respetuoso con la intimidad domiciliaria.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Las pausas y descansos se respetan igual o con más rigor (es donde más se vulneran).</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> El cómputo de horas extras requiere autorización previa, igual que en presencial.</li>
        </ul>
      </section>

      <section id="desconexion" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Derecho a la desconexión digital</h2>
        <p>
          El art. 18 de la Ley 10/2021 (en relación con el art. 88 LOPDGDD) consagra el <strong>derecho a la desconexión digital</strong>: el trabajador puede no atender comunicaciones laborales fuera de su jornada sin que ello tenga consecuencias.
        </p>
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg my-6">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 mb-1">Política de desconexión obligatoria</p>
              <p className="text-amber-700 text-sm">La empresa debe elaborar una política interna de desconexión, previa audiencia de la representación legal. Debe estar publicada y firmada por cada teletrabajador.</p>
            </div>
          </div>
        </div>
        <p>
          Un buen sistema de fichaje ayuda a hacer cumplir la desconexión: si el trabajador termina su jornada a las 18:00, las herramientas pueden recordárselo y bloquear notificaciones automáticas hasta el día siguiente.
        </p>
      </section>

      <section id="metodos" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Métodos de fichaje remoto recomendados</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2"><FiHome className="h-5 w-5 text-blue-600" /> App móvil con sesión autenticada</h3>
            <p className="text-sm text-gray-600">El método más común y menos invasivo. PIN o biometría del propio dispositivo, sin tracking.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2"><FiHome className="h-5 w-5 text-blue-600" /> Panel web</h3>
            <p className="text-sm text-gray-600">Botón de fichaje en el navegador. Útil para teletrabajadores que ya pasan el día en el ordenador.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2"><FiHome className="h-5 w-5 text-blue-600" /> Integración con SSO/Slack/Teams</h3>
            <p className="text-sm text-gray-600">Comando o atajo desde la herramienta de comunicación. Reduce fricción a casi cero.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2"><FiHome className="h-5 w-5 text-blue-600" /> Auto-fichaje al iniciar sesión</h3>
            <p className="text-sm text-gray-600">Detección automática de inicio/fin de sesión en el ordenador corporativo, con confirmación.</p>
          </div>
        </div>
      </section>

      <section id="privacidad" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Privacidad: lo que NO puedes hacer</h2>
        <p>
          La Ley 10/2021 y la jurisprudencia de la AEPD marcan líneas rojas claras:
        </p>
        <ul className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Geolocalización del domicilio.</strong> Prohibida. El domicilio es inviolable y no se puede vigilar a distancia.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Capturas de pantalla periódicas.</strong> Salvo justificación muy excepcional, vulnera la intimidad. La AEPD ha sancionado prácticas similares.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Activación remota de cámara o micrófono.</strong> Solo con consentimiento explícito y para finalidad concreta (videollamada).</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Registro de pulsaciones de teclado (keylogger).</strong> Práctica que la AEPD considera desproporcionada salvo casos muy graves de seguridad.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiShield className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div><strong>Uso del móvil personal sin acuerdo.</strong> Si necesitas que el trabajador use su teléfono, el acuerdo debe formalizar la compensación o proporcionar dispositivo.</div>
          </li>
        </ul>
      </section>

      <section id="acuerdo" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Qué debe contener el acuerdo de teletrabajo</h2>
        <p>El acuerdo escrito (obligatorio) debe incluir, entre otras cosas:</p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Inventario de medios proporcionados por la empresa.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Listado de gastos y forma de cuantificación y compensación.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Horario de trabajo y disponibilidad concreta.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Distribución entre trabajo presencial y a distancia.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Centro de trabajo de adscripción.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Lugar de teletrabajo y duración del acuerdo.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Medios de control empresarial y mecanismos de fichaje.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Procedimiento de reversibilidad.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Política de desconexión digital aplicable.</li>
        </ul>
      </section>

      <section id="caso-practico" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Caso práctico: pyme híbrida de 18 personas</h2>
        <div className="bg-gray-50 rounded-2xl p-6 my-6 border border-gray-100">
          <p className="font-semibold text-gray-900 mb-2">Situación</p>
          <p className="text-sm text-gray-600 mb-4">Empresa de software con oficina en Valencia, 18 empleados, 3 días remoto / 2 presencial. Sin sistema de fichaje formalizado, una hoja Excel mensual.</p>
          <p className="font-semibold text-gray-900 mb-2">Implementación recomendada</p>
          <ol className="space-y-1 text-sm text-gray-600 mb-4">
            <li>1. Sistema digital con app + web. App para los días de oficina, panel web para los de remoto. Si aún dudas qué solución encaja, repasa <Link to="/blog/como-elegir-sistema-fichaje">los criterios para elegir sistema de fichaje</Link>.</li>
            <li>2. Acuerdos de teletrabajo individuales firmados.</li>
            <li>3. Política de desconexión digital con horario 9:00-18:30 (con flexibilidad de ±1 h).</li>
            <li>4. Cómputo automático de horas semanales y aviso al empleado si supera 40 h.</li>
            <li>5. Compensación de gastos: 30 €/mes vía nómina.</li>
          </ol>
          <p className="font-semibold text-gray-900 mb-2">Resultado</p>
          <p className="text-sm text-gray-600">Cumplimiento RD-ley 8/2019 + Ley 10/2021 + RGPD. Tiempo administrativo reducido de 4 h/mes a 30 min/mes. Coste 27 €/mes (1,50 €/empleado).</p>
        </div>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        Para profundizar:
        <Link to="/blog/geolocalizacion-fichajes-rgpd" className="text-blue-600 hover:underline mx-1">geolocalización RGPD</Link>
        ·
        <Link to="/fichaje-digital" className="text-blue-600 hover:underline mx-1">fichaje digital</Link>.
      </div>
    </BlogLayout>
  )
}
